// Proving a phone number for the signed-in account (migration phone_ownership,
// edge function phone-link). GoTrue's updateUser({ phone }) cannot do this:
// with phone confirmations off it marks the number confirmed without a code.
// "Verified" on the client is always the server's answer (my_phone_status →
// phone_is_owned), never user.phone_confirmed_at.
import { sb, SUPABASE_ANON_KEY, SUPABASE_FUNCTIONS_URL } from '../supabase';

async function call(body, { anonymous = false } = {}) {
  const { data } = await sb.auth.getSession();
  // Reclaim needs no account (the code at the number is the proof).
  const token = data?.session?.access_token || (anonymous ? SUPABASE_ANON_KEY : null);
  if (!token) return { ok: false, error: 'sign_in_required' };
  try {
    const r = await fetch(`${SUPABASE_FUNCTIONS_URL}/phone-link`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, apikey: SUPABASE_ANON_KEY, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || !j.ok) console.warn('[phoneLink]', body.action, r.status, j.error || '');
    return { httpStatus: r.status, ...j };
  } catch (e) {
    console.error('[phoneLink] network:', e?.message || e);
    return { ok: false, error: 'network_error' };
  }
}

/** Send a code to `e164` for the signed-in account. → { ok } | { ok:false, error } */
export const startPhoneLink = (e164, lang) => call({ action: 'start', phone: e164, lang });

/** Check the code; on success the account's phone is that number and proven. */
export async function verifyPhoneLink(code) {
  const r = await call({ action: 'verify', code });
  if (r.ok) await sb.auth.refreshSession().catch(() => null);   // the session's user now carries the phone
  return r;
}

/**
 * Before a phone sign-in at checkout: how may this number be used?
 *   { ok:false, error:'use_signin' }   free, or a buyer's own proven number → the normal code
 *   { ok:true, sent:true }             held by an account that never proved it → OUR code was sent; reclaim
 *   { ok:false, error:'unavailable' }  proven by another kind of account, or an older unproven one
 */
export const claimStart = (e164, lang) => call({ action: 'claim_start', phone: e164, lang }, { anonymous: true });
/** The reclaim code typed back → the number is released for this person to sign up with. */
export const claimVerify = (e164, code) => call({ action: 'claim_verify', phone: e164, code }, { anonymous: true });

/** The caller's own phone and whether it is proven: { phone: digits|null, owned }. */
export async function myPhoneStatus() {
  const { data, error } = await sb.rpc('my_phone_status');
  if (error) { console.error('[phoneLink] my_phone_status:', error.message); return { phone: null, owned: false, error }; }
  return { phone: data?.phone || null, owned: Boolean(data?.owned) };
}

/** A phone-link error word → the key of the message to show. */
export function phoneLinkErrorKey(error) {
  switch (error) {
    case 'phone_in_use': return 'taken';
    case 'rate_limited': return 'rate';
    case 'send_failed': case 'network_error': case 'failed': return 'sendFailed';
    case 'sign_in_required': return 'signIn';
    case 'invalid_phone': return 'invalid';
    default: return 'badCode';   // bad_code, expired, too_many_attempts, no_code
  }
}
